/**
 * Query String Utilities
 *
 * Provides typed, reusable utilities for building and parsing URL query strings.
 * Used by both API clients and hooks for consistent parameter handling.
 */

/**
 * Configuration for a query string parameter
 */
export interface QueryParamConfig {
  /** Parameter key in URL */
  key: string;
  /** Type of value transformation */
  type: "string" | "number" | "boolean" | "array";
  /** Skip if value equals this (e.g., "any" for select filters) */
  skipValue?: string | number;
  /** Only include in query if value is truthy (for booleans) */
  onlyIfTrue?: boolean;
}

/**
 * Build a typed query string from parameters object
 *
 * @param params - Object with parameter values
 * @param config - Array of parameter configurations
 * @returns URL-encoded query string (without leading ?)
 *
 * @example
 * ```ts
 * const query = buildQueryString(
 *   { prefecture: "tokyo", rentMin: 50000, active: true },
 *   [
 *     { key: "prefecture", type: "string" },
 *     { key: "rentMin", type: "number" },
 *     { key: "active", type: "boolean", onlyIfTrue: true }
 *   ]
 * );
 * // "prefecture=tokyo&rentMin=50000&active=true"
 * ```
 */
export function buildQueryString<T extends Record<string, unknown>>(
  params: T,
  config: QueryParamConfig[],
): string {
  const searchParams = new URLSearchParams();

  for (const { key, type, skipValue, onlyIfTrue } of config) {
    const value = params[key];

    // Skip undefined/null values
    if (value === undefined || value === null) continue;

    // Skip if matches skipValue
    if (skipValue !== undefined && value === skipValue) continue;

    // For booleans with onlyIfTrue, skip if false
    if (type === "boolean" && onlyIfTrue && !value) continue;

    // Transform and add to params
    switch (type) {
      case "string":
        if (typeof value === "string" && value !== "") {
          searchParams.set(key, value);
        }
        break;
      case "number":
        if (typeof value === "number" || typeof value === "string") {
          searchParams.set(key, String(value));
        }
        break;
      case "boolean":
        if (value === true) {
          searchParams.set(key, "true");
        }
        break;
      case "array":
        if (Array.isArray(value) && value.length > 0) {
          searchParams.set(key, value.join(","));
        }
        break;
    }
  }

  return searchParams.toString();
}

/**
 * Parse a query string into typed parameters
 *
 * @param urlParams - URLSearchParams instance
 * @param config - Array of parameter configurations
 * @returns Typed parameters object
 *
 * @example
 * ```ts
 * const params = parseQueryString(
 *   new URLSearchParams("?prefecture=tokyo&rentMin=50000"),
 *   [
 *     { key: "prefecture", type: "string" },
 *     { key: "rentMin", type: "number" }
 *   ]
 * );
 * // { prefecture: "tokyo", rentMin: 50000 }
 * ```
 */
export function parseQueryString<T extends Record<string, unknown>>(
  urlParams: URLSearchParams,
  config: QueryParamConfig[],
): Partial<T> {
  const result: Record<string, unknown> = {};

  for (const { key, type } of config) {
    const value = urlParams.get(key);

    if (value === null) continue;

    switch (type) {
      case "string":
        result[key] = value;
        break;
      case "number": {
        const num = parseFloat(value);
        if (!isNaN(num)) {
          result[key] = num;
        }
        break;
      }
      case "boolean":
        result[key] = value === "true";
        break;
      case "array":
        result[key] = value.split(",").filter(Boolean);
        break;
    }
  }

  return result as Partial<T>;
}

/**
 * Create query param config for boolean filter keys
 *
 * @param keys - Array of boolean filter keys
 * @returns Array of QueryParamConfig for booleans
 */
export function createBooleanParamConfigs(
  keys: readonly string[],
): QueryParamConfig[] {
  return keys.map((key) => ({
    key,
    type: "boolean" as const,
    onlyIfTrue: true,
  }));
}

/**
 * Create query param config for string filter keys
 *
 * @param keys - Array of string filter keys
 * @param skipValue - Optional value to skip (e.g., "any")
 * @returns Array of QueryParamConfig for strings
 */
export function createStringParamConfigs(
  keys: readonly string[],
  skipValue?: string,
): QueryParamConfig[] {
  return keys.map((key) => ({
    key,
    type: "string" as const,
    skipValue,
  }));
}

/**
 * Create query param config for number filter keys
 *
 * @param keys - Array of number filter keys
 * @returns Array of QueryParamConfig for numbers
 */
export function createNumberParamConfigs(
  keys: readonly string[],
): QueryParamConfig[] {
  return keys.map((key) => ({
    key,
    type: "number" as const,
  }));
}
